/**
 * ReservationTypeInternalKey key-set — shared domain primitive (SCRUM-272 v1.1).
 *
 * The nine STABLE, system-retained catalog keys (DS-020 §2.10 / §4.2; widened
 * from seven to nine by SCRUM-366 P0 — +BOOKING +EXPEDIA, see the value-list
 * note below). Each
 * `internalKey` is the immutable identity of a reservation type in the GLOBAL
 * blueprint (the behavioral mapping `internalKey → stayKind / collectionModel /
 * defaultProcessor / displayName` lives in the booking module's
 * `stay-kind-mapping.ts`; only the key-set itself is lifted here).
 *
 * Why this lives in `@artemis/domain` (mirrors `revenue-distribution-method.ts`,
 * ADR 0027 shared-kernel for cross-BC value objects): TWO bounded contexts need
 * the key-set and they must NOT import each other's domain:
 *   - `booking` — the ReservationType catalog blueprint + the quote calculator
 *     match the stay's `internalKey` against a tax's allowlist.
 *   - `taxes-and-fees` — `TaxApplicability.reservationTypeAllowlist` is
 *     `ReservationTypeInternalKey[]`, validated at write-time (the single write
 *     chokepoint) against this key-set so an unknown key can never be silently
 *     persisted (DS-020 §11 money gate — no silent mis-key, no silent tax drop).
 *   - `@artemis/validators` — the tax Zod schemas re-export
 *     `RESERVATION_TYPE_INTERNAL_KEY_VALUES` as a `z.enum(...)`.
 *
 * `booking` already keeps a branded `ReservationTypeInternalKey` opaque id on its
 * Reservation aggregate (per-reservation identity). That is a DIFFERENT concept
 * — an opaque brand for one reservation's chosen type — and stays module-local.
 * This file is the ENUMERATED platform key-set used for membership validation.
 *
 * Boundary discipline (ADR 0011 / CLAUDE.md): pure TypeScript. No NestJS, no
 * Prisma, no Zod, no `new Date()`.
 */
/**
 * Canonical, ordered source-of-truth value list — the nine blueprint keys
 * (DS-020 §4.2; SCRUM-366 P0 widened from seven to nine — +BOOKING +EXPEDIA so
 * `channelCapability.reservationTypeKey` for BOO142/EXP270 resolves as a known
 * key and `TaxFeeConfigReadAdapter` stops fail-closing their tax/fee config).
 * Frozen so it cannot be mutated at runtime; the literal union and the named-key
 * record below are both derived from it. Every other enumeration site (the
 * booking blueprint, the tax Zod `z.enum`, the DB column) is the same nine
 * UPPER_SNAKE strings.
 */
export const RESERVATION_TYPE_INTERNAL_KEY_VALUES = Object.freeze([
    'OWNER_BLOCK',
    'GUEST_OF_OWNER',
    'GUEST_OF_OWNER_PAID',
    'PROPERTY_MANAGEMENT_COMPANY',
    'AIRBNB',
    'VRBO',
    'WEBSITE',
    'BOOKING',
    'EXPEDIA',
]);
/**
 * Named-key record (UPPER_SNAKE name → its literal string). Lets callers
 * reference a key by name (`RESERVATION_TYPE_INTERNAL_KEYS.AIRBNB`) instead of a
 * bare string literal. Derived from {@link RESERVATION_TYPE_INTERNAL_KEY_VALUES}
 * so the two can never drift.
 */
export const RESERVATION_TYPE_INTERNAL_KEYS = Object.freeze({
    OWNER_BLOCK: 'OWNER_BLOCK',
    GUEST_OF_OWNER: 'GUEST_OF_OWNER',
    GUEST_OF_OWNER_PAID: 'GUEST_OF_OWNER_PAID',
    PROPERTY_MANAGEMENT_COMPANY: 'PROPERTY_MANAGEMENT_COMPANY',
    AIRBNB: 'AIRBNB',
    VRBO: 'VRBO',
    WEBSITE: 'WEBSITE',
    BOOKING: 'BOOKING',
    EXPEDIA: 'EXPEDIA',
});
const VALID_VALUES = new Set(RESERVATION_TYPE_INTERNAL_KEY_VALUES);
/**
 * Type guard — `true` iff `x` is exactly one of the nine blueprint keys
 * (case-exact, trim-exact: NO whitespace stripping, NO lowercasing, NO glob /
 * regex expansion). A wildcard (`'*'`), an injection-shaped string, a near-miss
 * (`'AIRBN'` / `'AIRBNBX'`), a display-name form (`'Airbnb'`), or any non-string
 * all return `false`. This is the single membership predicate every consumer
 * (the tax VO write chokepoint, the booking catalog) validates against.
 */
export function isReservationTypeInternalKey(x) {
    return typeof x === 'string' && VALID_VALUES.has(x);
}
//# sourceMappingURL=reservation-type-internal-key.js.map